/**
 * @fullvsl/dashboard/analytics-client — data-source adapter (FUL-17).
 *
 * The dashboard never reads events/overview/breakdown directly; it goes through
 * an `AnalyticsDataSource`. This is the integration contract with the backend
 * analytics APIs (FUL-5 §9):
 *
 *   GET /api/v1/vsls/:id/analytics          → AnalyticsOverview
 *   GET /api/v1/vsls/:id/analytics/breakdown → AnalyticsBreakdown
 *
 * Two implementations ship:
 *   - `FixtureAnalyticsSource` (default): offline, derives everything from the
 *     seeded fixture event stream through the REAL backend aggregation, so the
 *     UI is pixel/data-identical to what the live API will return.
 *   - `HttpAnalyticsSource`: live client for the documented REST endpoints. It
 *     fetches overview + breakdown and, when the event-stream endpoint exists,
 *     recent events for the log. It degrades gracefully if events are not yet
 *     exposed (returns an empty list rather than throwing) so the dashboard
 *     stays usable during the staged backend rollout.
 *
 * Swap the source in `demo.ts` (`createDashboard({ source })`) — no other code
 * changes. This is the documented handoff boundary between FE and BE.
 */
import type {
  AnalyticsBreakdown,
  AnalyticsEvent,
  AnalyticsOverview,
  ID,
} from "../../contracts/src/contracts";
import { generateFixtureAnalytics, type FixtureAnalytics } from "./analytics-fixture";

export interface AnalyticsDataset {
  vslId: ID;
  overview: AnalyticsOverview;
  breakdown: AnalyticsBreakdown;
  /** Most recent events for the raw event-log table (empty when unavailable). */
  events: AnalyticsEvent[];
  /** Where the data came from — surfaced in the dashboard header. */
  source: "fixture" | "live";
}

export interface AnalyticsDataSource {
  load(vslId: ID): Promise<AnalyticsDataset>;
}

/**
 * Offline fixture source. Generates the seeded dataset on each load (cheap;
 * deterministic). Identical aggregation to the backend → the UI shown now is the
 * UI that will render against the live API later.
 */
export class FixtureAnalyticsSource implements AnalyticsDataSource {
  private readonly fixture: FixtureAnalytics;
  constructor(fixture?: FixtureAnalytics) {
    this.fixture = fixture ?? generateFixtureAnalytics();
  }
  async load(vslId: ID): Promise<AnalyticsDataset> {
    return {
      vslId,
      overview: this.fixture.overview,
      breakdown: this.fixture.breakdown,
      events: this.fixture.events,
      source: "fixture",
    };
  }
}

/**
 * Live HTTP source for the documented analytics endpoints.
 *
 * `baseUrl` is the FullVSL backend origin (no trailing slash). `fetchImpl` is
 * injectable for tests; defaults to the global. Errors are surfaced via the
 * rejected promise — the caller (dashboard) shows an inline error state.
 */
export class HttpAnalyticsSource implements AnalyticsDataSource {
  constructor(
    private readonly baseUrl: string,
    private readonly fetchImpl: typeof fetch = fetch,
  ) {}
  async load(vslId: ID): Promise<AnalyticsDataset> {
    const base = this.baseUrl.replace(/\/$/, "");
    const [overview, breakdown] = await Promise.all([
      this.getJson<AnalyticsOverview>(`${base}/api/v1/vsls/${vslId}/analytics`),
      this.getJson<AnalyticsBreakdown>(
        `${base}/api/v1/vsls/${vslId}/analytics/breakdown`,
      ),
    ]);
    // The event-stream list endpoint is part of the documented read surface but
    // may not be deployed in every environment. Degrade to an empty log rather
    // than failing the whole dashboard load.
    let events: AnalyticsEvent[] = [];
    try {
      events = await this.getJson<{ events: AnalyticsEvent[] }>(
        `${base}/api/v1/vsls/${vslId}/events?limit=12`,
      ).then((r) => r.events ?? []);
    } catch {
      events = [];
    }
    return { vslId, overview, breakdown, events, source: "live" };
  }

  private async getJson<T>(url: string): Promise<T> {
    const res = await this.fetchImpl(url, {
      headers: { accept: "application/json" },
    });
    if (!res.ok) {
      throw new Error(`analytics request failed: ${res.status} ${res.statusText}`);
    }
    return (await res.json()) as T;
  }
}
